import { now } from '../utils';

export interface GpuCompositeResult {
  shaderScore: number;
  textureScore: number;
  vertexScore: number;
  framebufferScore: number;
  compositeScore: number;
  totalTime: number;
  error?: string;
}

export class GpuCompositeFingerprint {
  private canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext | WebGL2RenderingContext | null;
  private program: WebGLProgram | null;
  private buffer: WebGLBuffer | null;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 128;
    this.canvas.height = 128;
    this.gl = null;
    this.program = null;
    this.buffer = null;
    this.initWebGL();
  }

  private initWebGL(): void {
    this.gl =
      (this.canvas.getContext('webgl2', {
        powerPreference: 'high-performance',
        antialias: false,
        preserveDrawingBuffer: false,
      }) as WebGL2RenderingContext | null) ||
      (this.canvas.getContext('webgl', {
        powerPreference: 'high-performance',
        antialias: false,
        preserveDrawingBuffer: false,
      }) as WebGLRenderingContext | null);

    if (!this.gl) {
      throw new Error('WebGL not supported');
    }

    this.setupShaders();
    this.setupGeometry();
  }

  private setupShaders(): void {
    const vs = `
attribute vec2 a_position;
void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
}`;
    const fs = `
precision mediump float;
uniform float u_time;
void main() {
    vec2 uv = gl_FragCoord.xy / 128.0;
    vec3 color = vec3(0.0);
    for (int i = 0; i < 8; i++) {
        float f = float(i);
        uv = abs(uv) - 0.5;
        float r = length(uv);
        float a = atan(uv.y, uv.x);
        uv.x = r * cos(a * 3.0 + u_time + f * 0.1);
        uv.y = r * sin(a * 3.0 + u_time + f * 0.1);
        color.r += 0.1 * sin(uv.x * 5.0);
        color.g += 0.1 * cos(uv.y * 4.0);
    }
    gl_FragColor = vec4(color, 1.0);
}`;
    this.program = this.createProgram(vs, fs);
    if (!this.program) {
      throw new Error('Failed to create GPU composite program');
    }
  }

  private createProgram(vs: string, fs: string): WebGLProgram | null {
    const gl = this.gl!;
    const vertexShader = this.compileShader(gl.VERTEX_SHADER, vs);
    const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, fs);
    if (!vertexShader || !fragmentShader) return null;

    const program = gl.createProgram();
    if (!program) return null;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  private compileShader(type: number, source: string): WebGLShader | null {
    const gl = this.gl!;
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private setupGeometry(): void {
    const gl = this.gl!;
    const vertices = new Float32Array([-1.0, -1.0, 1.0, -1.0, 0.0, 1.0]);
    this.buffer = gl.createBuffer();
    if (!this.buffer) throw new Error('Failed to create position buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
  }

  private gpuBarrier(): void {
    const gl = this.gl!;
    if (typeof (gl as WebGL2RenderingContext).finish === 'function') {
      (gl as WebGL2RenderingContext).finish();
      return;
    }
    const pixel = new Uint8Array(4);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
  }

  private measureOperation(operation: () => void): number {
    operation();
    const startTime = now();
    const targetOperations = 20;
    for (let i = 0; i < targetOperations; i++) {
      operation();
    }
    const duration = now() - startTime;
    return targetOperations / (duration || 1);
  }

  private testShaderComputation(): void {
    const gl = this.gl!;
    const program = this.program!;
    gl.useProgram(program);
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer!);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1f(timeLocation, now() / 1000);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  private testTextureOperations(): void {
    const gl = this.gl!;
    const size = 64;
    const texture = gl.createTexture();
    if (!texture) return;
    const data = new Uint8Array(size * size * 4);
    for (let i = 0; i < data.length; i += 4) {
      data[i] = i % 255;
      data[i + 1] = (i * 2) % 255;
      data[i + 2] = (i * 3) % 255;
      data[i + 3] = 255;
    }
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.deleteTexture(texture);
  }

  private testVertexProcessing(): void {
    const gl = this.gl!;
    const program = this.program!;
    const vertexCount = 500;
    const vertices = new Float32Array(vertexCount * 2);
    for (let i = 0; i < vertices.length; i++) {
      vertices[i] = (i % 100) / 50 - 1;
    }
    const vbo = gl.createBuffer();
    if (!vbo) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.useProgram(program);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.POINTS, 0, vertexCount);
    gl.deleteBuffer(vbo);
  }

  private testFramebufferOperation(): void {
    const gl = this.gl!;
    const program = this.program!;
    const texture = gl.createTexture();
    const framebuffer = gl.createFramebuffer();
    if (!texture || !framebuffer) return;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 64, 64, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.useProgram(program);
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer!);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    gl.viewport(0, 0, 64, 64);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.deleteTexture(texture);
    gl.deleteFramebuffer(framebuffer);
  }

  generateFingerprint(): GpuCompositeResult {
    const startTime = now();
    try {
      const shaderScore = this.measureOperation(() => this.testShaderComputation());
      const textureScore = this.measureOperation(() => this.testTextureOperations());
      const vertexScore = this.measureOperation(() => this.testVertexProcessing());
      const framebufferScore = this.measureOperation(() => this.testFramebufferOperation());
      this.gpuBarrier();
      const compositeScore = Math.round(
        shaderScore * 0.4 + textureScore * 0.3 + vertexScore * 0.2 + framebufferScore * 0.1
      );
      return {
        shaderScore,
        textureScore,
        vertexScore,
        framebufferScore,
        compositeScore,
        totalTime: now() - startTime,
      };
    } catch (error) {
      return {
        shaderScore: 0,
        textureScore: 0,
        vertexScore: 0,
        framebufferScore: 0,
        compositeScore: 0,
        totalTime: now() - startTime,
        error: (error as Error).message,
      };
    }
  }

  cleanup(): void {
    if (this.program) this.gl?.deleteProgram(this.program);
    if (this.buffer) this.gl?.deleteBuffer(this.buffer);
    this.program = null;
    this.buffer = null;
  }
}

export function runGpuComposite(): GpuCompositeResult {
  const fp = new GpuCompositeFingerprint();
  try {
    return fp.generateFingerprint();
  } finally {
    fp.cleanup();
  }
}
